import {
  AggregationIcon,
  Button,
  FormControlSizes,
  HStack,
  IconButton,
  IconName,
  MixpanelEvent,
  MixpanelEventProperty,
  Text,
  Tooltip,
  useMixpanel,
} from '@talos/kyoko';
import { useCallback } from 'react';
import { useMarketSelectorContext } from './MarketSelectorContext';
import { CUSTOM_VALUE } from './tokens';

interface MarketSelectorTriggerProps {
  disabled: boolean;
  onOpen: () => void;
  placeholder: string;
  size: FormControlSizes;
}

export function MarketSelectorTrigger({ disabled, onOpen, placeholder, size }: MarketSelectorTriggerProps) {
  const {
    presetsByID,
    preset,
    selections,
    allPossibleSelectionsMade,
    selectAll,
    unselectAll,
    narrowModeConfig,
    disableAggregations,
  } = useMarketSelectorContext();
  const mixpanel = useMixpanel();

  const getLabel = useCallback(
    (value: string) => {
      if (value === CUSTOM_VALUE) {
        return 'Custom';
      }

      return presetsByID?.get(value)?.label ?? value;
    },
    [presetsByID]
  );

  const selectorValue = selections.length === 0 ? undefined : preset ? preset : CUSTOM_VALUE;

  return (
    <HStack gap={size < FormControlSizes.Default ? 'spacing4' : 'spacing8'}>
      <Button
        disabled={disabled}
        onClick={onOpen}
        justifyContent="flex-start"
        flex="1"
        size={size}
        data-testid="market-selector-main-select-button"
      >
        <HStack gap="spacing4" fontWeight="400" justifyContent="flex-start" w="100%">
          {!disableAggregations ? <AggregationIcon /> : null}
          {selectorValue ? <Text>{getLabel(selectorValue)}</Text> : <Text color="colorTextSubtle">{placeholder}</Text>}
        </HStack>
      </Button>

      <Tooltip tooltip="Add Market">
        <IconButton
          size={size}
          disabled={disabled}
          icon={IconName.Plus}
          onClick={() => {
            onOpen();
            narrowModeConfig.setShowMarketSelectorBlotter(true);
          }}
          data-testid="market-selector-add-market-button"
        />
      </Tooltip>
      <Tooltip tooltip={allPossibleSelectionsMade ? 'Clear All' : 'Select All'}>
        <IconButton
          size={size}
          disabled={disabled}
          icon={allPossibleSelectionsMade ? IconName.ClearMultiple : IconName.CheckMultiple}
          onClick={() => {
            if (allPossibleSelectionsMade) {
              mixpanel.track(MixpanelEvent.UpdateMarketSelection, {
                [MixpanelEventProperty.Method]: 'ClearAll',
              });
              unselectAll();
            } else {
              mixpanel.track(MixpanelEvent.UpdateMarketSelection, {
                [MixpanelEventProperty.Method]: 'SelectAll',
              });
              selectAll();
            }
          }}
          data-action={allPossibleSelectionsMade ? 'clear' : 'select'}
          data-testid="clear-all-select-all-button"
        />
      </Tooltip>
    </HStack>
  );
}
